"use client";

import styles from "./ResetPasswordNewPasswordInput.module.scss";
import { useForm, usePasswordValidation } from "@/hooks";
import { setFieldError, setFieldValue } from "@/stores";
import { PASSWORD_REQUIREMENTS } from "@/constants";
import {
  GreenCheckCircleIcon,
  RedXCircleIcon,
} from "@/components";

import { useTranslations } from "@/i18n";
import {
  Fieldset,
  Flex,
  Input,
} from "@vetty/ui";

/**
 * ResetPasswordNewPasswordInput Component
 *
 * New password input field for reset password form with validation and error handling.
 * Manages new password field state and clears errors on user input.
 */
export const ResetPasswordNewPasswordInput = () => {
  const t = useTranslations("resetPassword.form");

  const { errors, values } = useForm();
  const { passwordChecks } = usePasswordValidation(
    String(values.password ?? "")
  );

  /**
   * Handles new password input changes
   * Updates field value and clears any existing errors
   */
  const handleInputChange = (field: string, value: string) => {
    setFieldValue(field, value);
    setFieldError(field, "");
  };

  return (
    <Fieldset isError={errors.password} errorMessage={errors.password}>
      <Flex direction={"column"} gap={8}>
        {/* New password input */}
        <Input
          id={"password"}
          label={t("password")}
          type={"password"}
          onChange={(v) => handleInputChange("password", v)}
        />

        {/* Password requirements */}
        <Flex
          direction={"column"}
          gap={10}
          className={styles["comp__requirements"]}
        >
          {Object.entries(PASSWORD_REQUIREMENTS).map(([key, req]) => {
            const passed = passwordChecks[key];
            const Icon = passed ? GreenCheckCircleIcon : RedXCircleIcon;

            return (
              <Flex key={key} align={"center"} gap={8}>
                <Icon />
                <span>{req.label}</span>
              </Flex>
            );
          })}
        </Flex>
      </Flex>
    </Fieldset>
  );
};
